import { dispatchSetMultipleState } from "@reducers/stateReducer";
import { dispatchSetMultipleUI } from "@reducers/uiReducer";
import { logger } from "@services/workers/helpers";
import { serviceAgreement } from "@src/utilities/objects";
import type { RightioWBSAPIModelsAccountCodes } from "@utils/apis/wbs/model";
import HistoryUtils from "@utils/history";
import job from "@utils/objects/job";
import Version from "@utils/version";
import { questionsUtils } from "../..";
import { api, state, ui } from "../../interfaces/interfaces";
import bookingCycleMgr from "../bookingCycleMgr";
import Queue from "../queue";
import StorageUtils from "../storage";
import { serviceManager } from "../workers";

interface NavigationHistory extends History {
	state: {
		idx: number;
		key: string;
		usr: {
			jobId: string;
			callFrom?: string;
			delayedLoad?: boolean;
			accountCode?: string;
		};
	};
}

type HistoryType = {
	location: Location;
	history: NavigationHistory;
};

const name = "BookingFormLoader";

const initCallMeBack = async (signal: AbortSignal) => {
	const callMeBack = StorageUtils.getFromStorage({
		type: "local",
		store: "state",
		key: "callMeBack",
	});
	// try to fix phone number, remove + and spaces, remove non-numeric, if starts with 447, replace with 07, if starts with 1,2,3,4,5,6, replace with 01,02,03,04,05,06
	const phoneNumber = callMeBack.contactNumber
		.replace(/\s/g, "")
		.replace(/\+/g, "")
		.replace(/\D/g, "")
		.replace(/^0440/g, "")
		.replace(/^044/g, "")
		.replace(/^44/g, "")
		.replace(/^1/g, "01")
		.replace(/^2/g, "02")
		.replace(/^3/g, "03")
		.replace(/^4/g, "04")
		.replace(/^5/g, "05")
		.replace(/^6/g, "06")
		.replace(/^7/g, "07");

	await job.init(callMeBack.contactNumber, null, null, signal);
	// check if phone number is mobile or landline
	let landline = "";
	let mobile = "";
	if (phoneNumber.startsWith("07")) {
		mobile = phoneNumber;
	} else {
		landline = phoneNumber;
	}
	dispatchSetMultipleState({
		JobCustomerCLI: phoneNumber,
		JobCustomerForename: callMeBack.forename.trim(),
		JobCustomerSurname: callMeBack.surname.trim(),
		JobEmailAddress: callMeBack.email.trim(),
		JobTelephoneMobile: mobile,
		JobTelephoneLandline: landline,
	});
	dispatchSetMultipleUI({
		PropertyContactMobile: mobile,
		PropertyContactLandline: landline,
		PropertyContactFirstName: callMeBack.forename.trim(),
		PropertyContactLastName: callMeBack.surname.trim(),
		PropertyContactEmail: callMeBack.email.trim(),
	});
};

const initNewBooking = async (
	accountCode: RightioWBSAPIModelsAccountCodes,
	sessionId: string,
	eventId: number,
	signal: AbortSignal,
	callFrom?: string,
) => {
	await job.init(callFrom ? `0${callFrom}` : "", sessionId, eventId, signal);
	bookingCycleMgr.setCurrentBookingJobId(state.JobId);
	dispatchSetMultipleState({
		Account: accountCode,
		CallNotes: Version.appName(),
	});
	// logger.debug(name, "CALL: ", call);
};

const setBookingId = async (
	ac: number,
	sessionId: string,
	eventId: number,
	signal: AbortSignal,
	callFrom?: string,
) => {
	const accountCode = await api.fetchAccountCode({
		accountCode: ac ? Number(ac) : 44999,
	});
	if (accountCode) {
		if (signal.aborted) {
			logger.debug(name + "->" + setBookingId.name, "signal aborted");
			return;
		}
		if (bookingCycleMgr.getCurrentBookingType() === "callMeBack") {
			await initCallMeBack(signal);
		} else {
			await initNewBooking(accountCode, sessionId, eventId, signal, callFrom);
		}
	}
	await job.saveIndexedDBOnExit({
		page: "BookingLoad",
		action: "setBookingId",
		message: "Booking saved",
		reason: "Initiation",
		booking: state.JobId,
	});
	serviceManager.sendMessageToWorker({
		serviceName: "BOOKING_DATA_SYNC",
		serviceData: {
			command: "SET_BOOKING_ID",
			origin: "MAIN_THREAD",
			payload: state.JobId,
		},
	});
	return "Booking ID set";
};

const isJobInStorage = () => {
	const job = StorageUtils.getFromStorage({
		type: "local",
		store: "state",
		key: "JobObject",
	});
	if (job?._jobId) {
		logger.debug(name + "->" + isJobInStorage.name, "Job found in storage");
		return job?._jobId;
	}
	return 0;
};

const loadBooking = async (browsing: HistoryType, signal: AbortSignal) => {
	const { history } = browsing;
	const { jobId: historyJobId, delayedLoad, callFrom } = history.state.usr;

	// set current bookingCycleMgr booking to loading
	bookingCycleMgr.setCurrentBookingLoading();

	if (!delayedLoad) {
		// init global settings
		questionsUtils.reset();
		// init serviceAgreement
		serviceAgreement.reset();
	}

	// if state has jobId most likely it was page refresh, we hydrate state for booking now
	// if (state.JobId) {
	//   await job.loadIndexedDB(state.JobId, async () => {
	//   });
	// }

	// load addresses for job postcode
	if (state.JobPostcode) {
		await api.addressSearch({ postcode: state.JobPostcode, scope: "Reload" });
	}

	// if job object has no jobId, check if there is a job in local storage
	if (state.JobId < 1) {
		const jobId =
			bookingCycleMgr.getCurrentBookingJobId() ||
			isJobInStorage() ||
			historyJobId;

		// if there is a job in local storage, load it
		if (jobId) {
			// found job id and will attempt to load it from indexedDB
			logger.debug(
				name + "->" + loadBooking.name,
				"Job found in storage, loading",
			);
			// change location only if path does not match and jobId does not match
			await job.loadIndexedDB(jobId, async () => {
				browsing.location.pathname !== "/booking-form" && jobId !== historyJobId
					? window.history.replaceState({ jobId: jobId }, "", "booking-form")
					: logger.debug(name, "No location change");
			});
			logger.debug(name + "->" + loadBooking.name, "Job loaded from storage");
			bookingCycleMgr.setCurrentBookingJobId(jobId);
		}
		// if there is no job in local storage, set a booking id for new job
		else {
			// get account code from local storage
			const ac =
				StorageUtils.getFromStorage({
					type: "session",
					store: "temp",
					key: "accountCode",
				}) || bookingCycleMgr.getCurrentBookingAccountCode();
			await setBookingId(
				ac,
				bookingCycleMgr.getCurrentBookingSessionId(),
				bookingCycleMgr.getCurrentBookingEventId(),
				signal,
				callFrom,
			);
			StorageUtils.saveToStorage({
				type: "session",
				store: "temp",
				key: "jobId",
				value: null,
			});
		}
	}
	// if history has a callFrom
	if (callFrom) {
		// create phone Number and pad with 0
		const phoneNumber = `0${callFrom}`;

		const body: {
			postcode?: string | undefined;
			property?: string | undefined;
			telephoneMobile?: string | undefined;
			telephoneLandline?: string | undefined;
			emailAddress?: string | undefined;
		} = {};

		// check if phone number is mobile or landline
		if (phoneNumber.startsWith("07")) {
			state.JobTelephoneMobile = phoneNumber;
			ui.PropertyContactMobile = phoneNumber;
			ui.CallFrom = phoneNumber;
			body.telephoneMobile = phoneNumber;
		} else {
			state.JobTelephoneLandline = phoneNumber;
			ui.PropertyContactLandline = phoneNumber;
			ui.CallFrom = phoneNumber;
			body.telephoneLandline = phoneNumber;
		}
		state.JobCustomerCLI = phoneNumber;
		await HistoryUtils.checkHistory(body);
	}
	bookingCycleMgr.setCurrentBookingPending();
	ui.Done = true;
	ui.Loading = false;
	StorageUtils.saveToStorage({
		type: "session",
		store: "temp",
		key: "spinner",
		value: { disabled: false },
	});
};

const runQueue = async (queue: Queue) => {
	// get current booking from bookingCycleMgr
	await queue.runParallel();
	bookingCycleMgr.setCurrentBookingPending();
	ui.Done = true;
	ui.Loading = false;
	StorageUtils.saveToStorage({
		type: "session",
		store: "temp",
		key: "spinner",
		value: { disabled: false },
	});
};

const onFirstRender = async (
	browsing: HistoryType,
	signal: AbortSignal,
): Promise<void> => {
	// log render
	logger.debug(name + "->" + onFirstRender.name, "First render");
	ui.Done = false;
	ui.Loading = true;
	StorageUtils.saveToStorage({
		type: "session",
		store: "temp",
		key: "spinner",
		value: { disabled: true },
	});
	const queue = new Queue();
	// new booking
	bookingCycleMgr.setCurrentBookingLoading();
	if (
		bookingCycleMgr.getCurrentBookingStatus() === "unknown" &&
		(bookingCycleMgr.getCurrentBookingType() === "manual" ||
			bookingCycleMgr.getCurrentBookingType() === "call" ||
			bookingCycleMgr.getCurrentBookingType() === "callMeBack")
	) {
		if (browsing.history.state.usr?.callFrom) {
			queue.push(
				setBookingId(
					bookingCycleMgr.getCurrentBookingAccountCode(),
					bookingCycleMgr.getCurrentBookingSessionId(),
					bookingCycleMgr.getCurrentBookingEventId(),
					signal,
					browsing.history.state.usr.callFrom,
				),
			);
		} else {
			queue.push(
				setBookingId(
					bookingCycleMgr.getCurrentBookingAccountCode(),
					bookingCycleMgr.getCurrentBookingSessionId(),
					bookingCycleMgr.getCurrentBookingEventId(),
					signal,
				),
			);
		}
		await runQueue(queue);
	} else {
		// old booking
		await loadBooking(browsing, signal);
	}
};

const getHistory = () => {
	// get window location
	// create history object
	const browsing: HistoryType = {
		location: window.location,
		history: window.history,
	};

	// add new properties to history
	// add state to location
	// combine window history and location into single object and return
	return browsing;
};

export const bookingFormLoader = (signal: AbortSignal) => {
	const browsing = getHistory();
	onFirstRender(browsing, signal);
};

export default bookingFormLoader;
